import type { GraphQLClient } from 'graphql-request'

import type { AgentStyle } from '~/game/agentCharacters'

// Interactive operations — reading a room's real conversation, posting into it,
// DMing a person, and task actions. Every operation mirrors the EXACT GraphQL
// the production apps use (Nova useChatsPage / zunou-mcp), never invented:
// content is Slate-JSON wrapped, team sends resolve team_thread ids, DMs go
// through the ONETOONE pulse 1:1 (the legacy DM path was removed 2026-08-07).

/** Wrap plain text in the Slate rich-text envelope the API requires. When a
 *  mention is given, the message leads with a real @-mention node (Nova's
 *  shape: name lives ON the node, empty text child) so the API creates the
 *  mention notification and clients render the pill. */
export function slateWrap(
  text: string,
  mention?: { id: string; name: string },
): string {
  const children: unknown[] = mention
    ? [
        {
          type: 'mention',
          mention: { id: mention.id, name: mention.name },
          children: [{ text: '' }],
        },
        { text: ` ${text}` },
      ]
    : [{ text }]
  return JSON.stringify([{ type: 'paragraph', children }])
}

/** Pull plain text back out of a Slate/JSON message body (best effort). */
// Message bodies arrive in many shapes (Slate JSON, system {message,ui}
// objects, HTML, digests…) — messageText.ts handles them all and never
// returns raw JSON. Aliased to the old name for existing call sites.
import { messageText as slateText } from './messageText'

export { slateText }

// --- Room messages ----------------------------------------------------------

const ROOM_MESSAGES_QUERY = /* GraphQL */ `
  query GlassOfficeRoomMessages($pulseId: ID!, $organizationId: ID, $page: Int, $topicId: ID) {
    teamThreadMessages(
      pulseId: $pulseId
      organizationId: $organizationId
      page: $page
      topicId: $topicId
    ) {
      teamThreadId
      data {
        id
        userId
        topicId
        content
        createdAt
        user { id name picture gravatar }
        topic { id name }
      }
      paginatorInfo { currentPage hasMorePages total }
    }
  }
`

export interface RoomMessage {
  id: string
  userId: string | null
  name: string
  avatar: string | null
  text: string
  createdAt: string
  topicId: string | null
  topicName: string | null
}

export interface RoomConversation {
  teamThreadId: string | null
  messages: RoomMessage[]
  total: number
  hasMorePages: boolean
}

interface RoomMessagesResponse {
  teamThreadMessages?: {
    teamThreadId?: string | null
    data?: Array<{
      id: string
      userId?: string | null
      topicId?: string | null
      content?: string | null
      createdAt?: string | null
      user?: { id: string; name?: string | null; picture?: string | null; gravatar?: string | null } | null
      topic?: { id: string; name?: string | null } | null
    }> | null
    paginatorInfo?: { currentPage: number; hasMorePages: boolean; total: number } | null
  } | null
}

export async function fetchRoomMessages(
  client: GraphQLClient,
  params: {
    pulseId: string
    organizationId: string
    page?: number
    topicId?: string | null
  },
): Promise<RoomConversation> {
  const data = await client.request<RoomMessagesResponse>(ROOM_MESSAGES_QUERY, {
    pulseId: params.pulseId,
    organizationId: params.organizationId,
    page: params.page ?? 1,
    topicId: params.topicId ?? null,
  })
  const block = data.teamThreadMessages
  const messages = (block?.data ?? [])
    .map((m) => ({
      id: m.id,
      userId: m.user?.id ?? m.userId ?? null,
      name: m.user?.name ?? 'Someone',
      avatar: m.user?.picture || m.user?.gravatar || null,
      text: slateText(m.content),
      createdAt: m.createdAt ?? '',
      topicId: m.topic?.id ?? m.topicId ?? null,
      topicName: m.topic?.name ?? null,
    }))
    // API returns newest-first; render oldest-first like a chat.
    .reverse()
  return {
    teamThreadId: block?.teamThreadId ?? null,
    messages,
    total: block?.paginatorInfo?.total ?? messages.length,
    hasMorePages: block?.paginatorInfo?.hasMorePages ?? false,
  }
}

// --- Send a message into a room ---------------------------------------------

const CREATE_TEAM_MESSAGE = /* GraphQL */ `
  mutation GlassOfficeSendTeamMessage($input: CreateTeamMessageInput!) {
    createTeamMessage(input: $input) {
      id
      teamThreadId
      createdAt
    }
  }
`

export async function sendRoomMessage(
  client: GraphQLClient,
  params: {
    teamThreadId: string
    userId: string
    text: string
    topicId?: string | null
    /** Lead the message with a real @-mention of this person. */
    mention?: { id: string; name: string }
  },
): Promise<{ id: string; teamThreadId: string }> {
  const res = await client.request<{
    createTeamMessage?: { id?: string | null; teamThreadId?: string | null } | null
  }>(CREATE_TEAM_MESSAGE, {
    input: {
      teamThreadId: params.teamThreadId,
      userId: params.userId,
      content: slateWrap(params.text, params.mention),
      ...(params.topicId ? { topicId: params.topicId } : {}),
    },
  })
  return {
    id: res.createTeamMessage?.id ?? '',
    teamThreadId: res.createTeamMessage?.teamThreadId ?? params.teamThreadId,
  }
}

// --- Ping / message a person ------------------------------------------------
// Everything in Zunou is a team chat: TEAM pulses, ONETOONE pulses for 1:1s,
// and NETWORK pulses for cross-org connections. The legacy DirectMessage system
// is retired (its dashboard page is commented out) — never write to it. A
// ping/message therefore always lands in a PULSE: the one the person was
// clicked in, a shared team chat, or the 1:1 (created on demand).

const CREATE_ONETOONE_PULSE = /* GraphQL */ `
  mutation GlassOfficeCreateDmPulse($input: ProvisionPulseInput!) {
    createPulse(input: $input) {
      id
    }
  }
`

const TOGGLE_TEAM_REACTION = /* GraphQL */ `
  mutation GlassOfficeToggleReaction($input: ToggleTeamMessageReactionInput!) {
    toggleTeamMessageReaction(input: $input)
  }
`

interface PulseLite {
  id: string
  name: string
  category: string
  memberUserIds: string[]
  teamThreadId?: string | null
}

/** Where a ping/message can go. `pulseId: null` = the 1:1 doesn't exist yet and
 *  is created on send. `here` marks the pulse the person was clicked in. */
export interface MessageDestination {
  key: string
  label: string
  pulseId: string | null
  here?: boolean
  /** The 1:1 — no @-mention needed there. */
  dm?: boolean
}

/** The places you can reach this person from, most-relevant first: the pulse
 *  you clicked them in (if you share it), the 1:1, then other shared teams. */
export function buildMessageDestinations(params: {
  pulses: PulseLite[]
  meId: string
  targetUserId: string
  targetName: string
  contextPulseId?: string | null
}): MessageDestination[] {
  const { pulses, meId, targetUserId, contextPulseId } = params
  const shared = (p: PulseLite) =>
    p.memberUserIds.includes(meId) && p.memberUserIds.includes(targetUserId)
  const out: MessageDestination[] = []
  const seen = new Set<string>()

  const context = contextPulseId
    ? pulses.find((p) => p.id === contextPulseId && shared(p))
    : undefined
  if (context) {
    out.push({ key: context.id, label: context.name, pulseId: context.id, here: true })
    seen.add(context.id)
  }

  const dm = pulses.find((p) => p.category === 'ONETOONE' && shared(p))
  if (dm && !seen.has(dm.id)) {
    out.push({ key: dm.id, label: 'Direct message', pulseId: dm.id, dm: true })
    seen.add(dm.id)
  } else if (!dm) {
    out.push({ key: '__new_dm__', label: 'Direct message', pulseId: null, dm: true })
  }

  for (const p of pulses) {
    if (seen.has(p.id) || p.category !== 'TEAM' || !shared(p)) continue
    out.push({ key: p.id, label: p.name, pulseId: p.id })
    seen.add(p.id)
  }
  return out
}

/** Resolve a destination to a concrete pulse + thread, creating the 1:1 if the
 *  destination is the not-yet-existing direct message. */
async function resolveDestination(
  client: GraphQLClient,
  params: {
    destination: MessageDestination
    organizationId: string
    targetUserId: string
    targetName: string
    pulses: PulseLite[]
  },
): Promise<{
  pulseId: string
  pulseName: string
  teamThreadId: string
  /** ONETOONE for DMs (existing or just created) — mentions are skipped there. */
  category: string
}> {
  const { destination, organizationId, targetUserId, targetName, pulses } = params
  let pulseId = destination.pulseId
  const pulse = pulseId ? pulses.find((p) => p.id === pulseId) : undefined
  let teamThreadId = pulse?.teamThreadId ?? null

  if (!pulseId) {
    const created = await client.request<{
      createPulse?: { id?: string | null } | null
    }>(CREATE_ONETOONE_PULSE, {
      input: {
        pulse: { category: 'ONETOONE', name: targetName, organizationId },
        members: [{ role: 'STAFF', userId: targetUserId }],
      },
    })
    pulseId = created.createPulse?.id ?? null
    if (!pulseId) throw new Error('Could not create the 1:1 conversation')
  }
  if (!teamThreadId) {
    const conv = await fetchRoomMessages(client, { pulseId, organizationId })
    teamThreadId = conv.teamThreadId
  }
  if (!teamThreadId) throw new Error('That conversation has no message thread')
  return {
    pulseId,
    pulseName: pulse?.name ?? destination.label,
    teamThreadId,
    category: pulse?.category ?? 'ONETOONE',
  }
}

/** Notify hooks so this layer stays hub-agnostic — GameView wires them to the
 *  real notification-hub client. */
export interface DeliveryNotify {
  message?: (a: {
    pulseId: string
    pulseName: string
    messageId: string
    preview: string
    teamThreadId: string
    mentionedUserIds: string[]
  }) => void
  reaction?: (a: { pulseId: string; messageId: string; emoji: string }) => void
}

/** Post a message to the chosen destination (creating the 1:1 if needed). */
export async function deliverMessage(
  client: GraphQLClient,
  params: {
    destination: MessageDestination
    organizationId: string
    meId: string
    targetUserId: string
    targetName: string
    pulses: PulseLite[]
    text: string
  },
  notify?: DeliveryNotify,
): Promise<void> {
  const { pulseId, pulseName, teamThreadId, category } = await resolveDestination(
    client,
    params,
  )
  // In a shared pulse the message is FOR someone specific — lead with a real
  // @-mention so they're properly notified. A 1:1 is already addressed.
  const mention =
    category === 'ONETOONE'
      ? undefined
      : { id: params.targetUserId, name: params.targetName }
  const sent = await sendRoomMessage(client, {
    teamThreadId,
    userId: params.meId,
    text: params.text,
    mention,
  })
  if (sent.id) {
    notify?.message?.({
      pulseId,
      pulseName,
      messageId: sent.id,
      preview: mention ? `@${mention.name} ${params.text}` : params.text,
      teamThreadId,
      mentionedUserIds: mention ? [mention.id] : [],
    })
  }
}

/** Ping = react with an emoji on the person's most recent message in the chosen
 *  destination. If they have no message there (or it's a brand-new 1:1), fall
 *  back to posting the emoji as a message so the ping still lands. */
export async function pingPerson(
  client: GraphQLClient,
  params: {
    destination: MessageDestination
    organizationId: string
    meId: string
    targetUserId: string
    targetName: string
    pulses: PulseLite[]
    emoji: string
  },
  notify?: DeliveryNotify,
): Promise<{ mode: 'reacted' | 'messaged'; pulseName: string }> {
  const { destination, organizationId, targetUserId, emoji } = params
  // A not-yet-created 1:1 has nothing to react to — go straight to a message.
  if (destination.pulseId) {
    const conv = await fetchRoomMessages(client, {
      pulseId: destination.pulseId,
      organizationId,
    })
    const theirs = [...conv.messages].reverse().find((m) => m.userId === targetUserId)
    if (theirs) {
      await client.request(TOGGLE_TEAM_REACTION, {
        input: { teamMessageId: theirs.id, reaction: emoji },
      })
      notify?.reaction?.({ pulseId: destination.pulseId, messageId: theirs.id, emoji })
      const pulse = params.pulses.find((p) => p.id === destination.pulseId)
      return { mode: 'reacted', pulseName: pulse?.name ?? destination.label }
    }
  }
  await deliverMessage(client, { ...params, text: emoji }, notify)
  const pulse = destination.pulseId
    ? params.pulses.find((p) => p.id === destination.pulseId)
    : undefined
  return { mode: 'messaged', pulseName: pulse?.name ?? destination.label }
}

// --- Room tasks ---------------------------------------------------------------
// Zunou pulses organize work as LIST containers with the real tasks as
// CHILDREN (the server returns only parent_id-NULL rows by default, children
// nested under them). A flat type:TASK query therefore misses almost
// everything — always walk the tree.

const TASK_NODE_FIELDS = /* GraphQL */ `
  id
  task_number
  title
  description
  status
  type
  priority
  due_date
  task_status_id
  taskStatus { id label color }
  assignees { id user { id name picture gravatar } }
`

const ROOM_TASKS_QUERY = /* GraphQL */ `
  query GlassOfficeRoomTasks($organizationId: String!, $entityId: String) {
    tasks(organizationId: $organizationId, entityId: $entityId) {
      ${TASK_NODE_FIELDS}
      children {
        ${TASK_NODE_FIELDS}
        children { ${TASK_NODE_FIELDS} }
      }
    }
  }
`

export interface RoomTask {
  id: string
  number: string | null
  title: string
  description: string | null
  /** Legacy enum: TODO | INPROGRESS | COMPLETED | OVERDUE | CANCELLED. */
  status: string
  /** TASK | LIST | MILESTONE. */
  type: string
  /** Custom pulse status when configured (takes display precedence). */
  statusLabel: string | null
  statusColor: string | null
  priority: string | null
  dueDate: string | null
  /** The LIST this task lives in (null = loose top-level task). */
  listId: string | null
  listName: string | null
  assignees: Array<{ userId: string; name: string; avatar: string | null }>
}

export interface RoomTaskBoard {
  /** Real work items (lists themselves excluded), children flattened in. */
  tasks: RoomTask[]
  /** The pulse's LIST containers with open/total counts. */
  lists: Array<{ id: string; name: string; open: number; total: number }>
}

interface TaskNode {
  id: string
  task_number?: string | null
  title: string
  description?: string | null
  status?: string | null
  type?: string | null
  priority?: string | null
  due_date?: string | null
  task_status_id?: string | null
  taskStatus?: { id: string; label?: string | null; color?: string | null } | null
  assignees?: Array<{
    id: string
    user?: { id: string; name?: string | null; picture?: string | null; gravatar?: string | null } | null
  }> | null
  children?: TaskNode[] | null
}

interface RoomTasksResponse {
  tasks?: TaskNode[] | null
}

function mapTask(t: TaskNode, listId: string | null, listName: string | null): RoomTask {
  return {
    id: t.id,
    number: t.task_number ?? null,
    title: t.title,
    description: t.description ?? null,
    status: (t.status ?? 'TODO').toUpperCase(),
    type: (t.type ?? 'TASK').toUpperCase(),
    statusLabel: t.taskStatus?.label ?? null,
    statusColor: t.taskStatus?.color ?? null,
    priority: t.priority ?? null,
    dueDate: t.due_date ?? null,
    listId,
    listName,
    assignees: (t.assignees ?? [])
      .filter((a) => a.user?.id)
      .map((a) => ({
        userId: a.user!.id,
        name: a.user!.name ?? 'Someone',
        avatar: a.user!.picture || a.user!.gravatar || null,
      })),
  }
}

const isOpenStatus = (s: string | null | undefined) =>
  s !== 'COMPLETED' && s !== 'CANCELLED'

/** Walk the list→task→subtask tree into a flat board. */
export function flattenTaskTree(rows: TaskNode[] | null | undefined): RoomTaskBoard {
  const tasks: RoomTask[] = []
  const lists: RoomTaskBoard['lists'] = []
  const walk = (node: TaskNode, listId: string | null, listName: string | null) => {
    tasks.push(mapTask(node, listId, listName))
    for (const child of node.children ?? []) walk(child, listId, listName)
  }
  for (const row of rows ?? []) {
    const type = (row.type ?? 'TASK').toUpperCase()
    if (type === 'LIST' || type === 'MILESTONE') {
      // Containers are structure, not work (mirrors fetchTasksForPulse) —
      // rendering a MILESTONE as a cyclable card mutates the container.
      const kids = row.children ?? []
      lists.push({
        id: row.id,
        name: row.title,
        total: kids.length,
        open: kids.filter((c) => isOpenStatus((c.status ?? 'TODO').toUpperCase())).length,
      })
      for (const child of kids) walk(child, row.id, row.title)
    } else {
      walk(row, null, null)
    }
  }
  return { tasks, lists }
}

export async function fetchRoomTasks(
  client: GraphQLClient,
  params: { organizationId: string; pulseId: string },
): Promise<RoomTaskBoard> {
  const data = await client.request<RoomTasksResponse>(ROOM_TASKS_QUERY, {
    organizationId: params.organizationId,
    entityId: params.pulseId,
  })
  return flattenTaskTree(data.tasks)
}

const PERSON_TASKS_QUERY = /* GraphQL */ `
  query GlassOfficePersonTasks($organizationId: String!, $assigneeId: String) {
    tasks(organizationId: $organizationId, assigneeId: $assigneeId) {
      ${TASK_NODE_FIELDS}
      children {
        ${TASK_NODE_FIELDS}
        children { ${TASK_NODE_FIELDS} }
      }
    }
  }
`

/**
 * Everything assigned to one person across the org (their real workload).
 * The assignee filter matches parents whose CHILDREN match too, so flatten
 * and re-filter client-side to keep only rows truly assigned to the person.
 */
export async function fetchPersonTasks(
  client: GraphQLClient,
  params: { organizationId: string; userId: string },
): Promise<RoomTask[]> {
  const data = await client.request<RoomTasksResponse>(PERSON_TASKS_QUERY, {
    organizationId: params.organizationId,
    assigneeId: params.userId,
  })
  const { tasks } = flattenTaskTree(data.tasks)
  return tasks.filter(
    (t) => t.type !== 'LIST' && t.assignees.some((a) => a.userId === params.userId),
  )
}

// --- Pulse board columns (custom task statuses) --------------------------------

const PULSE_TASK_STATUSES_QUERY = /* GraphQL */ `
  query GlassOfficeTaskStatuses($pulseId: ID!) {
    taskStatuses(pulseId: $pulseId) {
      id
      label
      color
      system_type
      position
    }
  }
`

export interface PulseTaskStatus {
  id: string
  label: string
  color: string | null
  /** START | MIDDLE | END — maps to TODO | INPROGRESS | COMPLETED. */
  systemType: string | null
  position: number
}

/** The pulse's kanban columns — needed to keep the board in sync on status changes. */
export async function fetchPulseTaskStatuses(
  client: GraphQLClient,
  pulseId: string,
): Promise<PulseTaskStatus[]> {
  const data = await client.request<{
    taskStatuses?: Array<{
      id: string
      label?: string | null
      color?: string | null
      system_type?: string | null
      position?: number | string | null
    }> | null
  }>(PULSE_TASK_STATUSES_QUERY, { pulseId })
  return (data.taskStatuses ?? [])
    .map((s) => ({
      id: s.id,
      label: s.label ?? '',
      color: s.color ?? null,
      systemType: s.system_type ? String(s.system_type).toUpperCase() : null,
      position: s.position != null ? Number(s.position) : 0,
    }))
    .sort((a, b) => a.position - b.position)
}

/** The board column a legacy status should land in (START/MIDDLE/END mapping). */
export function boardColumnForStatus(
  columns: PulseTaskStatus[],
  status: string,
): PulseTaskStatus | null {
  const want =
    status === 'COMPLETED' ? 'END' : status === 'INPROGRESS' ? 'MIDDLE' : 'START'
  return columns.find((c) => c.systemType === want) ?? null
}

// --- Agent observability (what an agent has actually been doing) ---------------

const AGENT_ACTIVITIES_QUERY = /* GraphQL */ `
  query GlassOfficeAgentActivities($agentAccountId: ID!, $first: Int) {
    agentActivities(agentAccountId: $agentAccountId, first: $first) {
      id
      type
      summary
      label
      status
      createdAt
    }
  }
`

export interface AgentActivity {
  id: string
  type: string
  summary: string | null
  // Humanized line computed at API ingestion (AGENT_PRESENCE_SPEC.md);
  // null on pre-translator history rows.
  label: string | null
  status: string | null
  createdAt: string
}

/** Recent activity stream for an agent (org owners; newest first). */
export async function fetchAgentActivities(
  client: GraphQLClient,
  agentAccountId: string,
  first = 8,
): Promise<AgentActivity[]> {
  const data = await client.request<{
    agentActivities?: Array<{
      id: string
      type?: string | null
      summary?: string | null
      label?: string | null
      status?: string | null
      createdAt?: string | null
    }> | null
  }>(AGENT_ACTIVITIES_QUERY, { agentAccountId, first })
  return (data.agentActivities ?? []).map((a) => ({
    id: a.id,
    type: a.type ?? 'activity',
    summary: a.summary ?? null,
    label: a.label ?? null,
    status: a.status ?? null,
    createdAt: a.createdAt ?? '',
  }))
}

const AGENT_RECENT_MESSAGES_QUERY = /* GraphQL */ `
  query GlassOfficeAgentMessages($agentAccountId: ID!, $first: Int) {
    agentRecentMessages(agentAccountId: $agentAccountId, first: $first) {
      id
      content
      createdAt
      topic { id name }
    }
  }
`

export interface AgentMessagePeek {
  id: string
  text: string
  topicName: string | null
  createdAt: string
}

/** Recent messages an agent has posted (org owners; newest first). */
export async function fetchAgentRecentMessages(
  client: GraphQLClient,
  agentAccountId: string,
  first = 5,
): Promise<AgentMessagePeek[]> {
  const data = await client.request<{
    agentRecentMessages?: Array<{
      id: string
      content?: string | null
      createdAt?: string | null
      topic?: { id: string; name?: string | null } | null
    }> | null
  }>(AGENT_RECENT_MESSAGES_QUERY, { agentAccountId, first })
  return (data.agentRecentMessages ?? []).map((m) => ({
    id: m.id,
    text: slateText(m.content),
    topicName: m.topic?.name ?? null,
    createdAt: m.createdAt ?? '',
  }))
}

const CREATE_TASK = /* GraphQL */ `
  mutation GlassOfficeCreateTask($input: [CreateTaskInput!]) {
    createTask(input: $input) {
      id
      task_number
      title
    }
  }
`

export async function createRoomTask(
  client: GraphQLClient,
  params: {
    organizationId: string
    pulseId: string
    title: string
    assignees?: string[]
    /** Create inside this LIST container (task lists group a pulse's work). */
    parentId?: string | null
  },
): Promise<void> {
  await client.request(CREATE_TASK, {
    input: [
      {
        title: params.title,
        organization_id: params.organizationId,
        entity_id: params.pulseId,
        entity_type: 'PULSE',
        task_type: 'TASK',
        ...(params.parentId ? { parent_id: params.parentId } : {}),
        ...(params.assignees?.length ? { assignees: params.assignees } : {}),
      },
    ],
  })
}

const UPDATE_TASK_STATUS = /* GraphQL */ `
  mutation GlassOfficeUpdateTaskStatus($input: UpdateTaskStatusInput!) {
    updateTaskStatus(input: $input) {
      id
      status
    }
  }
`

export async function setTaskStatus(
  client: GraphQLClient,
  params: {
    organizationId: string
    taskId: string
    status: string
    /**
     * The matching kanban column (see boardColumnForStatus). updateTaskStatus
     * sets fields verbatim without syncing, so omitting this leaves the card
     * in its old dashboard column while the enum moves on.
     */
    taskStatusId?: string | null
  },
): Promise<void> {
  await client.request(UPDATE_TASK_STATUS, {
    input: {
      taskId: params.taskId,
      organization_id: params.organizationId,
      status: params.status,
      ...(params.taskStatusId ? { task_status_id: params.taskStatusId } : {}),
    },
  })
}

const UPDATE_TASK_ASSIGNEES = /* GraphQL */ `
  mutation GlassOfficeAssignTask($input: UpdateTaskInput!) {
    updateTask(input: $input) {
      id
      assignees { id user { id name } }
    }
  }
`

export async function setTaskAssignees(
  client: GraphQLClient,
  params: { organizationId: string; taskId: string; title: string; assignees: string[] },
): Promise<void> {
  await client.request(UPDATE_TASK_ASSIGNEES, {
    input: {
      taskId: params.taskId,
      // updateTask is a full update; title is required alongside assignees.
      title: params.title,
      organization_id: params.organizationId,
      assignees: params.assignees,
    },
  })
}

const EDIT_TASK = /* GraphQL */ `
  mutation GlassOfficeEditTask($input: UpdateTaskInput!) {
    updateTask(input: $input) {
      id
      title
      description
      due_date
    }
  }
`

/** Full task edit (updateTask is a PUT — title always required). */
export async function editTask(
  client: GraphQLClient,
  params: {
    organizationId: string
    taskId: string
    title: string
    description?: string | null
    dueDate?: string | null
    assignees?: string[]
  },
): Promise<void> {
  await client.request(EDIT_TASK, {
    input: {
      taskId: params.taskId,
      organization_id: params.organizationId,
      title: params.title,
      ...(params.description !== undefined ? { description: params.description } : {}),
      ...(params.dueDate !== undefined ? { due_date: params.dueDate } : {}),
      ...(params.assignees ? { assignees: params.assignees } : {}),
    },
  })
}

/** The visible next step in the simple TODO → doing → done loop. */
export function nextTaskStatus(status: string): string {
  if (status === 'TODO' || status === 'OVERDUE') return 'INPROGRESS'
  if (status === 'INPROGRESS') return 'COMPLETED'
  return 'TODO'
}

// --- Hire an AI agent ---------------------------------------------------------
// The dashboard's exact provisioning flow (org OWNER only): addAgentToOrganization
// creates the Auth0 identity + users row (kind AGENT) + org membership + MCP
// permission baseline, and returns the one-time MCP credential; createPulseMember
// then seats the agent in the room's pulse.

const ADD_AGENT = /* GraphQL */ `
  mutation GlassOfficeHireAgent($input: AddAgentInput!) {
    addAgentToOrganization(input: $input) {
      agentAccount {
        id
        userId
        status
        user { id name kind }
      }
      credential { email password }
    }
  }
`

const CREATE_PULSE_MEMBER = /* GraphQL */ `
  mutation GlassOfficeJoinPulse($pulseId: String!, $input: [CreatePulseMemberInput!]) {
    createPulseMember(pulseId: $pulseId, input: $input) {
      id
      userId
    }
  }
`

export interface HiredAgent {
  agentAccountId: string
  userId: string
  name: string
  /** One-time MCP login for the new agent (also revealable in the dashboard). */
  credential: { email: string; password: string } | null
  /** Whether seating them in the room's pulse succeeded. */
  joinedRoom: boolean
  /** Why seating failed, when it did (e.g. not a room admin). */
  joinError: string | null
}

// --- Manage an existing agent -------------------------------------------------

// `steward` is newer than the rest: an API from before it refuses the field,
// and the query retries without it (the Steward is then unknown, not off).
const agentAccountsQuery = (steward: boolean): string => /* GraphQL */ `
  query GlassOfficeAgentAccounts($organizationId: ID!) {
    agentAccounts(organizationId: $organizationId) {
      id
      userId
      status
      runState
      canManageAgents
      roleKind
      hiringManager { id name }
      ${steward ? 'steward { principalName staff }' : ''}
      jobTitle
      jobDescription
      instructions
      skills { id slug name }
      adapters { id slug name }
      office { id name }
      user { id name }
      presence { state summary label lastHeartbeatAt }
    }
  }
`

/** True when the API is older than a selection: it refuses one of `fields`. */
function refusesField(e: unknown, fields: string[]): boolean {
  if (!(e instanceof Error)) return false
  // graphql-request carries the server's message inside JSON, so the quotes
  // around the field name may arrive escaped: \"steward\" as well as "steward".
  return fields.some((field) =>
    new RegExp(`Cannot query field \\\\?"${field}\\\\?"`).test(e.message),
  )
}

export interface AgentAttachment {
  id: string
  slug: string
  name: string
}

export interface AgentAccountLite {
  id: string
  userId: string
  name: string
  /** Normalized uppercase: ACTIVE | SUSPENDED | RETIRED. */
  status: string
  /** Normalized uppercase: RUNNING | PAUSED | STOPPED — the box-control state. */
  runState: string
  /** The HR manager — this agent can hire/seat/skill other agents. */
  canManageAgents: boolean
  /** STAFF | EA | PERSONAL. A PERSONAL agent runs on its owner's own computer,
   *  as them: the teal robot. */
  roleKind: string
  /** Its hiring manager: for a PERSONAL agent, the owner whose computer it runs on. */
  hiringManagerName: string | null
  /** Steward on: whose account it looks after (the principal) and whether it
   *  has the staff tier. null = off; undefined = an older API cannot say. */
  steward: AgentStewardLite | null | undefined
  jobTitle: string | null
  jobDescription: string | null
  /** The agent's live CLAUDE.md instructions (versioned server-side). */
  instructions: string | null
  /** Skills attached to this agent (.claude/skills/<slug> on the box). */
  skills: AgentAttachment[]
  /** Adapters attached to this agent (.claude/adapters/<slug> on the box). */
  adapters: AgentAttachment[]
  /** The office (box) this agent lives in — null means it can't run. */
  office: { id: string; name: string } | null
  /** Live runtime snapshot from the box (ONLINE/WORKING/IDLE/BLOCKED/OFFLINE). */
  presence: { state: string | null; summary: string | null; label: string | null; lastHeartbeatAt: string | null } | null
}

export interface AgentStewardLite {
  principalName: string
  staff: boolean
}

/** All the org's agent accounts (org-owner-gated query). */
export async function fetchAgentAccountsAll(
  client: GraphQLClient,
  organizationId: string,
): Promise<AgentAccountLite[]> {
  type Row = {
    id: string
    userId: string
    status?: string | null
    runState?: string | null
    canManageAgents?: boolean | null
    roleKind?: string | null
    hiringManager?: { id: string; name?: string | null } | null
    steward?: { principalName?: string | null; staff?: boolean | null } | null
    jobTitle?: string | null
    jobDescription?: string | null
    instructions?: string | null
    skills?: Array<{ id: string; slug?: string | null; name?: string | null }> | null
    adapters?: Array<{ id: string; slug?: string | null; name?: string | null }> | null
    office?: { id: string; name?: string | null } | null
    user?: { id: string; name?: string | null } | null
    presence?: {
      state?: string | null
      summary?: string | null
      label?: string | null
      lastHeartbeatAt?: string | null
    } | null
  }
  const load = (steward: boolean) =>
    client.request<{ agentAccounts?: Row[] | null }>(agentAccountsQuery(steward), {
      organizationId,
    })
  let withSteward = true
  const data = await load(true).catch((e: unknown) => {
    if (!refusesField(e, ['steward'])) throw e
    withSteward = false
    return load(false)
  })
  const mapAttachment = (
    rows: Array<{ id: string; slug?: string | null; name?: string | null }> | null | undefined,
  ): AgentAttachment[] =>
    (rows ?? []).map((r) => ({ id: r.id, slug: r.slug ?? '', name: r.name ?? r.slug ?? '' }))
  return (data.agentAccounts ?? []).map((row) => ({
    id: row.id,
    userId: row.userId,
    name: row.user?.name ?? 'Agent',
    // API returns PHP enum case names (e.g. "Active"); normalize to uppercase.
    status: String(row.status ?? 'ACTIVE').toUpperCase(),
    runState: String(row.runState ?? 'STOPPED').toUpperCase(),
    canManageAgents: Boolean(row.canManageAgents),
    roleKind: String(row.roleKind ?? 'STAFF').toUpperCase(),
    hiringManagerName: row.hiringManager?.name ?? null,
    steward: !withSteward
      ? undefined
      : row.steward
        ? { principalName: row.steward.principalName ?? '', staff: Boolean(row.steward.staff) }
        : null,
    jobTitle: row.jobTitle ?? null,
    jobDescription: row.jobDescription ?? null,
    instructions: row.instructions ?? null,
    skills: mapAttachment(row.skills),
    adapters: mapAttachment(row.adapters),
    office: row.office ? { id: row.office.id, name: row.office.name ?? 'Office' } : null,
    presence: row.presence
      ? {
          state: row.presence.state ?? null,
          summary: row.presence.summary ?? null,
          label: row.presence.label ?? null,
          lastHeartbeatAt: row.presence.lastHeartbeatAt ?? null,
        }
      : null,
  }))
}

// --- Agent looks (member-visible) ---------------------------------------------
// The looks for EVERY viewer (the role's emblem: HR, PA, EA, Steward ★ and
// its staff tier's red ★; the stored look: character, colour, hat, cape,
// badge, with the cape and badge styles; and unplugged) from the agent-dock
// presence feed (trust-filtered like the dock), not the owner-only
// agentAccounts query.

const LOOKS_BASE = 'userId funding { kind }'
const LOOKS_STEWARD = `${LOOKS_BASE} roleKind canManageAgents steward stewardStaff`
// Newest first: each older API refuses what the level above adds. An API
// from before cape and badge styles lacks capeStyle/badgeStyle (the booleans
// still draw the red cape and the star); one from before the stored looks
// lacks look/readiness (everyone is the classic robot, nobody unplugged); one
// from before Steward lacks steward/stewardStaff; one from before the looks
// lacks roleKind/canManageAgents too (personal still reads from funding.kind
// PERSONAL; HR and EA are unknown).
const LOOKS_SELECTIONS = [
  `${LOOKS_STEWARD} look { character color hat cape badge capeStyle badgeStyle } readiness { state }`,
  `${LOOKS_STEWARD} look { character color hat cape badge } readiness { state }`,
  LOOKS_STEWARD,
  `${LOOKS_BASE} roleKind canManageAgents`,
  LOOKS_BASE,
]
const agentLooksQuery = (selection: string): string => /* GraphQL */ `
  query GlassOfficeAgentLooks($organizationId: ID!) {
    orgAgentPresence(organizationId: $organizationId) {
      ${selection}
    }
  }
`

/** One source's view of the looks: user ids, as full sets. null = this source cannot say. */
export interface AgentLooks {
  /** HR managers (the HR emblem). */
  hr: string[] | null
  /** Personal agents (the PA emblem, teal by default). */
  personal: string[]
  /** EAs (the EA emblem, coral by default). */
  ea: string[] | null
  /** Stewards (the ★ emblem). */
  steward: string[] | null
  /** Stewards with the staff tier (the red ★): a subset of steward. */
  stewardStaff: string[] | null
  /** Stored looks by user id; an agent without one is the classic robot. */
  styles: Record<string, AgentStyle> | null
  /** Agents whose readiness is UNPLUGGED. */
  unplugged: string[] | null
}

/** The member-visible looks, stepping down to what an older API accepts. */
export async function fetchAgentLooks(
  client: GraphQLClient,
  organizationId: string,
): Promise<AgentLooks> {
  type Row = {
    userId: string
    roleKind?: string | null
    canManageAgents?: boolean | null
    steward?: boolean | null
    stewardStaff?: boolean | null
    funding?: { kind?: string | null } | null
    look?: AgentStyle | null
    readiness?: { state?: string | null } | null
  }
  const kindOf = (r: Row): string => String(r.roleKind ?? '').toUpperCase()
  const personalOf = (r: Row): boolean =>
    kindOf(r) === 'PERSONAL' ||
    String(r.funding?.kind ?? '').toUpperCase() === 'PERSONAL'
  const ids = (rows: Row[], pick: (r: Row) => boolean): string[] =>
    rows.filter(pick).map((r) => r.userId)
  for (let level = 0; ; level++) {
    let rows: Row[]
    try {
      const data = await client.request<{ orgAgentPresence?: Row[] | null }>(
        agentLooksQuery(LOOKS_SELECTIONS[level]),
        { organizationId },
      )
      rows = data.orgAgentPresence ?? []
    } catch (e) {
      const last = level >= LOOKS_SELECTIONS.length - 1
      if (last || !(e instanceof Error) || !/Cannot query field/i.test(e.message)) throw e
      continue
    }
    const styled = level <= 1
    const steward = level <= 2
    const looks = level <= 3
    return {
      hr: looks ? ids(rows, (r) => Boolean(r.canManageAgents)) : null,
      personal: ids(rows, personalOf),
      ea: looks ? ids(rows, (r) => kindOf(r) === 'EA') : null,
      steward: steward ? ids(rows, (r) => Boolean(r.steward)) : null,
      stewardStaff: steward
        ? ids(rows, (r) => Boolean(r.steward) && Boolean(r.stewardStaff))
        : null,
      styles: styled
        ? Object.fromEntries(
            rows.flatMap((r) => (r.look ? [[r.userId, r.look] as const] : [])),
          )
        : null,
      unplugged: styled
        ? ids(rows, (r) => String(r.readiness?.state ?? '').toUpperCase() === 'UNPLUGGED')
        : null,
    }
  }
}

/** A source's looks plus when they were read. */
export interface AgentLooksSource extends AgentLooks {
  at: number
}

/** The looks the floor wears: full sets. */
export interface MergedAgentLooks {
  hr: string[]
  personal: string[]
  ea: string[]
  steward: string[]
  stewardStaff: string[]
  styles: Record<string, AgentStyle>
  unplugged: string[]
}

/**
 * Merge the looks sources (the presence feed for everyone, agentAccounts for
 * owners): personal when EITHER source says so; everything else from the
 * fresher source that can say (a source with null is skipped). Steward and
 * its staff tier come from the same source, so the red star never outlives
 * Steward.
 */
export function mergeAgentLooks(
  sources: Array<AgentLooksSource | undefined>,
): MergedAgentLooks {
  const present = sources.filter((s): s is AgentLooksSource => s !== undefined)
  const freshest = (
    field: 'hr' | 'ea' | 'steward' | 'styles' | 'unplugged',
  ): AgentLooksSource | undefined =>
    present.filter((s) => s[field] !== null).sort((a, b) => b.at - a.at)[0]
  const stewardSource = freshest('steward')
  const steward = stewardSource?.steward ?? []
  return {
    hr: freshest('hr')?.hr ?? [],
    personal: [...new Set(present.flatMap((s) => s.personal))],
    ea: freshest('ea')?.ea ?? [],
    steward,
    stewardStaff: (stewardSource?.stewardStaff ?? []).filter((id) => steward.includes(id)),
    styles: freshest('styles')?.styles ?? {},
    unplugged: freshest('unplugged')?.unplugged ?? [],
  }
}

/** The agent account behind a user, if any (org-owner-gated query). */
export async function fetchAgentAccount(
  client: GraphQLClient,
  params: { organizationId: string; userId: string },
): Promise<AgentAccountLite | null> {
  const all = await fetchAgentAccountsAll(client, params.organizationId)
  return all.find((a) => a.userId === params.userId) ?? null
}

// --- Agent brain: instructions, skills, adapters --------------------------------
// Instructions = the agent's live CLAUDE.md (versioned). Skills/adapters are
// org-library markdown attached per agent; the office box materializes them
// under .claude/ on the next sync. Same ops the dashboard uses.

export interface LibraryItem {
  id: string
  slug: string
  name: string
  description: string | null
  /** Normalized uppercase: DRAFT | ACTIVE | ARCHIVED. */
  status: string
}

const SKILLS_QUERY = /* GraphQL */ `
  query GlassOfficeSkills($organizationId: ID!) {
    skills(organizationId: $organizationId) {
      id
      slug
      name
      description
      status
    }
  }
`

const ADAPTERS_QUERY = /* GraphQL */ `
  query GlassOfficeAdapters($organizationId: ID!) {
    adapters(organizationId: $organizationId) {
      id
      slug
      name
      description
      status
    }
  }
`

function mapLibrary(
  rows:
    | Array<{
        id: string
        slug?: string | null
        name?: string | null
        description?: string | null
        status?: string | null
      }>
    | null
    | undefined,
): LibraryItem[] {
  return (rows ?? [])
    .map((r) => ({
      id: r.id,
      slug: r.slug ?? '',
      name: r.name ?? r.slug ?? '',
      description: r.description ?? null,
      status: String(r.status ?? 'ACTIVE').toUpperCase(),
    }))
    .filter((r) => r.status !== 'ARCHIVED')
}

/** The org's skill library (own + global catalog). Org owners. */
export async function fetchSkills(
  client: GraphQLClient,
  organizationId: string,
): Promise<LibraryItem[]> {
  const data = await client.request<{ skills?: Parameters<typeof mapLibrary>[0] }>(
    SKILLS_QUERY,
    { organizationId },
  )
  return mapLibrary(data.skills)
}

/** The org's adapter library (own + global templates). Org owners. */
export async function fetchAdapters(
  client: GraphQLClient,
  organizationId: string,
): Promise<LibraryItem[]> {
  const data = await client.request<{ adapters?: Parameters<typeof mapLibrary>[0] }>(
    ADAPTERS_QUERY,
    { organizationId },
  )
  return mapLibrary(data.adapters)
}

const UPDATE_AGENT_INSTRUCTIONS = /* GraphQL */ `
  mutation GlassOfficeUpdateInstructions($agentAccountId: ID!, $content: String!, $note: String) {
    updateAgentInstructions(agentAccountId: $agentAccountId, content: $content, note: $note) {
      id
      instructions
    }
  }
`

/** Save the agent's CLAUDE.md — creates a new server-side version each call. */
export async function updateAgentInstructions(
  client: GraphQLClient,
  params: { agentAccountId: string; content: string; note?: string },
): Promise<void> {
  await client.request(UPDATE_AGENT_INSTRUCTIONS, {
    agentAccountId: params.agentAccountId,
    content: params.content,
    note: params.note ?? 'Edited in Glass Office',
  })
}

const ASSIGN_SKILL = /* GraphQL */ `
  mutation GlassOfficeAssignSkill($agentAccountId: ID!, $skillId: ID!) {
    assignSkillToAgent(agentAccountId: $agentAccountId, skillId: $skillId) {
      id
      skills { id slug name }
    }
  }
`
const UNASSIGN_SKILL = /* GraphQL */ `
  mutation GlassOfficeUnassignSkill($agentAccountId: ID!, $skillId: ID!) {
    unassignSkillFromAgent(agentAccountId: $agentAccountId, skillId: $skillId) {
      id
      skills { id slug name }
    }
  }
`
const ASSIGN_ADAPTER = /* GraphQL */ `
  mutation GlassOfficeAssignAdapter($agentAccountId: ID!, $adapterId: ID!) {
    assignAdapterToAgent(agentAccountId: $agentAccountId, adapterId: $adapterId) {
      id
      adapters { id slug name }
    }
  }
`
const UNASSIGN_ADAPTER = /* GraphQL */ `
  mutation GlassOfficeUnassignAdapter($agentAccountId: ID!, $adapterId: ID!) {
    unassignAdapterFromAgent(agentAccountId: $agentAccountId, adapterId: $adapterId) {
      id
      adapters { id slug name }
    }
  }
`

export async function setAgentSkill(
  client: GraphQLClient,
  params: { agentAccountId: string; skillId: string; attached: boolean },
): Promise<void> {
  await client.request(params.attached ? ASSIGN_SKILL : UNASSIGN_SKILL, {
    agentAccountId: params.agentAccountId,
    skillId: params.skillId,
  })
}

export async function setAgentAdapter(
  client: GraphQLClient,
  params: { agentAccountId: string; adapterId: string; attached: boolean },
): Promise<void> {
  await client.request(params.attached ? ASSIGN_ADAPTER : UNASSIGN_ADAPTER, {
    agentAccountId: params.agentAccountId,
    adapterId: params.adapterId,
  })
}

// Quick-create: new skills/adapters are ACTIVE immediately server-side, so
// they ship to boxes on the next sync. Slug rule: ^[a-z0-9-]+$ (max 120).

/** A box-safe slug from a human name; empty when nothing survives cleaning. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120)
}

const CREATE_SKILL = /* GraphQL */ `
  mutation GlassOfficeCreateSkill($input: CreateSkillInput!) {
    createSkill(input: $input) {
      id
      slug
      name
    }
  }
`

const CREATE_ADAPTER = /* GraphQL */ `
  mutation GlassOfficeCreateAdapter($input: CreateAdapterInput!) {
    createAdapter(input: $input) {
      id
      slug
      name
    }
  }
`

export async function createSkill(
  client: GraphQLClient,
  params: {
    organizationId: string
    name: string
    slug: string
    content: string
    description?: string
  },
): Promise<{ id: string }> {
  const data = await client.request<{ createSkill?: { id: string } }>(CREATE_SKILL, {
    input: {
      organizationId: params.organizationId,
      slug: params.slug,
      name: params.name,
      content: params.content,
      ...(params.description ? { description: params.description } : {}),
      note: 'Created in Glass Office',
    },
  })
  if (!data.createSkill?.id) throw new Error('Skill was not created')
  return { id: data.createSkill.id }
}

export async function createAdapter(
  client: GraphQLClient,
  params: {
    organizationId: string
    name: string
    slug: string
    content: string
    description?: string
  },
): Promise<{ id: string }> {
  const data = await client.request<{ createAdapter?: { id: string } }>(CREATE_ADAPTER, {
    input: {
      organizationId: params.organizationId,
      slug: params.slug,
      name: params.name,
      content: params.content,
      ...(params.description ? { description: params.description } : {}),
      note: 'Created in Glass Office',
    },
  })
  if (!data.createAdapter?.id) throw new Error('Adapter was not created')
  return { id: data.createAdapter.id }
}

// --- Agent training: learning modes, notebook, approval queue -------------------
// Agents learn on the job: lessons land in a NOTEBOOK gated by instruction mode
// (LEARNING applies now, PROPOSE queues for human review, LOCKED rejects), and
// agents can draft skills / propose skill updates. Same ops as the dashboard's
// Workforce section. All owner / office.manage gated — fail soft on 403.

export type InstructionMode = 'LEARNING' | 'PROPOSE' | 'LOCKED'

export interface NotebookVersion {
  id: string
  content: string
  /** APPLIED | PENDING | REJECTED */
  status: string
  /** AGENT | HUMAN */
  author: string
  createdAt: string
}

export interface AgentNotebookState {
  mode: InstructionMode
  applied: NotebookVersion | null
  pending: NotebookVersion[]
  historyCount: number
}

const AGENT_NOTEBOOK_QUERY = /* GraphQL */ `
  query GlassOfficeNotebook($agentAccountId: ID!) {
    agentNotebook(agentAccountId: $agentAccountId) {
      mode
      applied { id content status author createdAt }
      pending { id content status author createdAt }
      history { id }
    }
  }
`

interface NotebookVersionRow {
  id: string
  content?: string | null
  status?: string | null
  author?: string | null
  createdAt?: string | null
}

function mapVersion(v: NotebookVersionRow): NotebookVersion {
  return {
    id: v.id,
    content: v.content ?? '',
    status: String(v.status ?? 'PENDING').toUpperCase(),
    author: String(v.author ?? 'AGENT').toUpperCase(),
    createdAt: v.createdAt ?? '',
  }
}

export async function fetchAgentNotebook(
  client: GraphQLClient,
  agentAccountId: string,
): Promise<AgentNotebookState> {
  const data = await client.request<{
    agentNotebook?: {
      mode?: string | null
      applied?: NotebookVersionRow | null
      pending?: NotebookVersionRow[] | null
      history?: Array<{ id: string }> | null
    } | null
  }>(AGENT_NOTEBOOK_QUERY, { agentAccountId })
  const n = data.agentNotebook
  return {
    mode: (String(n?.mode ?? 'PROPOSE').toUpperCase() as InstructionMode) || 'PROPOSE',
    applied: n?.applied ? mapVersion(n.applied) : null,
    pending: (n?.pending ?? []).map(mapVersion),
    historyCount: (n?.history ?? []).length,
  }
}

const SET_INSTRUCTION_MODE = /* GraphQL */ `
  mutation GlassOfficeSetMode($agentAccountId: ID!, $mode: AgentInstructionMode!) {
    setAgentInstructionMode(agentAccountId: $agentAccountId, mode: $mode) {
      mode
    }
  }
`

export async function setInstructionMode(
  client: GraphQLClient,
  params: { agentAccountId: string; mode: InstructionMode },
): Promise<void> {
  await client.request(SET_INSTRUCTION_MODE, params)
}

const REVIEW_NOTEBOOK = /* GraphQL */ `
  mutation GlassOfficeReviewNotebook($versionId: ID!, $approve: Boolean!, $note: String) {
    reviewAgentNotebook(versionId: $versionId, approve: $approve, note: $note) {
      mode
    }
  }
`

export async function reviewNotebook(
  client: GraphQLClient,
  params: { versionId: string; approve: boolean; note?: string },
): Promise<void> {
  await client.request(REVIEW_NOTEBOOK, {
    versionId: params.versionId,
    approve: params.approve,
    note: params.note ?? null,
  })
}

/** Per-agent training/inbox snapshot for in-world badges (agentDebug). */
export interface AgentBrief {
  userId: string | null
  mode: InstructionMode | null
  pendingCount: number
  inboxCount: number
}

const AGENT_BRIEF_QUERY = /* GraphQL */ `
  query GlassOfficeAgentBrief($agentAccountId: ID!) {
    agentDebug(agentAccountId: $agentAccountId) {
      userId
      instructionMode
      pendingNotebookCount
      inbox {
        notifications { id }
        directThreads { threadId }
        oneToOneThreads { pulseId }
        replies { messageId }
      }
    }
  }
`

export async function fetchAgentBrief(
  client: GraphQLClient,
  agentAccountId: string,
): Promise<AgentBrief> {
  const data = await client.request<{
    agentDebug?: {
      userId?: string | null
      instructionMode?: string | null
      pendingNotebookCount?: number | null
      inbox?: {
        notifications?: Array<{ id: string }> | null
        directThreads?: Array<{ threadId: string }> | null
        oneToOneThreads?: Array<{ pulseId: string }> | null
        replies?: Array<{ messageId: string }> | null
      } | null
    } | null
  }>(AGENT_BRIEF_QUERY, { agentAccountId })
  const d = data.agentDebug
  const inbox = d?.inbox
  return {
    userId: d?.userId ?? null,
    mode: d?.instructionMode
      ? (String(d.instructionMode).toUpperCase() as InstructionMode)
      : null,
    pendingCount: d?.pendingNotebookCount ?? 0,
    inboxCount:
      (inbox?.notifications?.length ?? 0) +
      (inbox?.directThreads?.length ?? 0) +
      (inbox?.oneToOneThreads?.length ?? 0) +
      (inbox?.replies?.length ?? 0),
  }
}

/** Everything the org's agents are waiting on humans for (one call). */
export interface OrgAgentRequests {
  total: number
  notebooks: Array<{
    versionId: string
    content: string
    createdAt: string
    agentAccountId: string
    agentName: string | null
  }>
  skillDrafts: Array<{
    skillId: string
    skillName: string
    content: string
    agentAccountId: string | null
    agentName: string | null
  }>
  skillUpdates: Array<{
    proposalId: string
    skillName: string | null
    content: string
    currentContent: string | null
    note: string | null
    agentAccountId: string
    agentName: string | null
  }>
}

const AGENT_REQUESTS_QUERY = /* GraphQL */ `
  query GlassOfficeAgentRequests($organizationId: ID!) {
    agentRequests(organizationId: $organizationId) {
      total
      notebooks {
        agentAccountId
        agentName
        version { id content createdAt }
      }
      skillDrafts {
        agentAccountId
        agentName
        skill { id name content }
      }
      skillUpdates {
        agentAccountId
        agentName
        skillName
        currentContent
        proposal { id content note }
      }
    }
  }
`

export async function fetchAgentRequests(
  client: GraphQLClient,
  organizationId: string,
): Promise<OrgAgentRequests> {
  const data = await client.request<{
    agentRequests?: {
      total?: number | null
      notebooks?: Array<{
        agentAccountId: string
        agentName?: string | null
        version?: { id: string; content?: string | null; createdAt?: string | null } | null
      }> | null
      skillDrafts?: Array<{
        agentAccountId?: string | null
        agentName?: string | null
        skill?: { id: string; name?: string | null; content?: string | null } | null
      }> | null
      skillUpdates?: Array<{
        agentAccountId: string
        agentName?: string | null
        skillName?: string | null
        currentContent?: string | null
        proposal?: { id: string; content?: string | null; note?: string | null } | null
      }> | null
    } | null
  }>(AGENT_REQUESTS_QUERY, { organizationId })
  const r = data.agentRequests
  return {
    total: r?.total ?? 0,
    notebooks: (r?.notebooks ?? [])
      .filter((n) => n.version?.id)
      .map((n) => ({
        versionId: n.version!.id,
        content: n.version!.content ?? '',
        createdAt: n.version!.createdAt ?? '',
        agentAccountId: n.agentAccountId,
        agentName: n.agentName ?? null,
      })),
    skillDrafts: (r?.skillDrafts ?? [])
      .filter((d) => d.skill?.id)
      .map((d) => ({
        skillId: d.skill!.id,
        skillName: d.skill!.name ?? 'Untitled skill',
        content: d.skill!.content ?? '',
        agentAccountId: d.agentAccountId ?? null,
        agentName: d.agentName ?? null,
      })),
    skillUpdates: (r?.skillUpdates ?? [])
      .filter((u) => u.proposal?.id)
      .map((u) => ({
        proposalId: u.proposal!.id,
        skillName: u.skillName ?? null,
        content: u.proposal!.content ?? '',
        currentContent: u.currentContent ?? null,
        note: u.proposal!.note ?? null,
        agentAccountId: u.agentAccountId,
        agentName: u.agentName ?? null,
      })),
  }
}

const REVIEW_SKILL_UPDATE = /* GraphQL */ `
  mutation GlassOfficeReviewSkillUpdate($proposalId: ID!, $approve: Boolean!, $note: String) {
    reviewSkillUpdate(proposalId: $proposalId, approve: $approve, note: $note) {
      total
    }
  }
`

export async function reviewSkillUpdate(
  client: GraphQLClient,
  params: { proposalId: string; approve: boolean; note?: string },
): Promise<void> {
  await client.request(REVIEW_SKILL_UPDATE, {
    proposalId: params.proposalId,
    approve: params.approve,
    note: params.note ?? null,
  })
}

const SET_SKILL_STATUS = /* GraphQL */ `
  mutation GlassOfficeSetSkillStatus($input: UpdateSkillInput!) {
    updateSkill(input: $input) {
      id
      status
    }
  }
`

/** Workforce's draft review: approve = ACTIVE (+ attach), reject = ARCHIVED. */
export async function setSkillStatus(
  client: GraphQLClient,
  params: { skillId: string; status: 'ACTIVE' | 'ARCHIVED' },
): Promise<void> {
  await client.request(SET_SKILL_STATUS, {
    input: { skillId: params.skillId, status: params.status },
  })
}

// --- Office health (the box's live onboarding checklist) ------------------------

export interface OfficeHealth {
  officeId: string
  /** Worst step status: ACTION (red) > WARN (amber) > OK. PENDING reads OK. */
  level: 'OK' | 'WARN' | 'ACTION'
  /** Titles of the steps that need a human (WARN/ACTION only). */
  issues: string[]
}

const OFFICE_ONBOARDING_QUERY = /* GraphQL */ `
  query GlassOfficeOfficeHealth($officeId: ID!) {
    officeOnboarding(officeId: $officeId) {
      steps {
        title
        status
      }
    }
  }
`

export interface OfficeChecklistStep {
  key: string
  title: string
  /** OK | WARN | ACTION | PENDING */
  status: string
  detail: string
  /** Copy-paste commands to run on the box (empty when none needed). */
  commands: string[]
}

const OFFICE_CHECKLIST_QUERY = /* GraphQL */ `
  query GlassOfficeChecklist($officeId: ID!) {
    officeOnboarding(officeId: $officeId) {
      generatedAt
      steps {
        key
        title
        status
        detail
        commands
      }
    }
  }
`

/** The full office checklist — steps, human-readable detail, and the exact
 *  box commands to fix what's broken. Org owners. */
export async function fetchOfficeChecklist(
  client: GraphQLClient,
  officeId: string,
): Promise<{ generatedAt: string | null; steps: OfficeChecklistStep[] }> {
  const data = await client.request<{
    officeOnboarding?: {
      generatedAt?: string | null
      steps?: Array<{
        key?: string | null
        title?: string | null
        status?: string | null
        detail?: string | null
        commands?: string[] | null
      }> | null
    } | null
  }>(OFFICE_CHECKLIST_QUERY, { officeId })
  return {
    generatedAt: data.officeOnboarding?.generatedAt ?? null,
    steps: (data.officeOnboarding?.steps ?? []).map((s) => ({
      key: s.key ?? '',
      title: s.title ?? 'Step',
      status: String(s.status ?? 'OK').toUpperCase(),
      detail: s.detail ?? '',
      commands: s.commands ?? [],
    })),
  }
}

/** Live health of an office box — catches dead engine logins, stopped
 *  supervisors, missing permissions. Org owners. */
export async function fetchOfficeHealth(
  client: GraphQLClient,
  officeId: string,
): Promise<OfficeHealth> {
  const data = await client.request<{
    officeOnboarding?: {
      steps?: Array<{ title?: string | null; status?: string | null }> | null
    } | null
  }>(OFFICE_ONBOARDING_QUERY, { officeId })
  const steps = (data.officeOnboarding?.steps ?? []).map((s) => ({
    title: s.title ?? 'Step',
    status: String(s.status ?? 'OK').toUpperCase(),
  }))
  const level = steps.some((s) => s.status === 'ACTION')
    ? 'ACTION'
    : steps.some((s) => s.status === 'WARN')
      ? 'WARN'
      : 'OK'
  return {
    officeId,
    level,
    issues: steps
      .filter((s) => s.status === 'ACTION' || s.status === 'WARN')
      .map((s) => s.title),
  }
}

// --- Offices (the box runtime an agent lives in) -------------------------------

const OFFICES_QUERY = /* GraphQL */ `
  query GlassOfficeOffices($organizationId: ID!) {
    offices(organizationId: $organizationId) {
      id
      name
      status
      seats
      lastSeenAt
    }
  }
`

export interface OfficeLite {
  id: string
  name: string
  /** Normalized uppercase (ACTIVE | SUSPENDED | ARCHIVED | PENDING…). */
  status: string
  seats: number
  lastSeenAt: string | null
}

/** The org's offices (org owners only). An agent needs one to actually run. */
export async function fetchOffices(
  client: GraphQLClient,
  organizationId: string,
): Promise<OfficeLite[]> {
  const data = await client.request<{
    offices?: Array<{
      id: string
      name: string
      status?: string | null
      seats?: number | null
      lastSeenAt?: string | null
    }> | null
  }>(OFFICES_QUERY, { organizationId })
  return (data.offices ?? []).map((o) => ({
    id: o.id,
    name: o.name,
    status: String(o.status ?? '').toUpperCase(),
    seats: o.seats ?? 0,
    lastSeenAt: o.lastSeenAt ?? null,
  }))
}

const ASSIGN_AGENT_TO_OFFICE = /* GraphQL */ `
  mutation GlassOfficeAssignAgent($agentAccountId: ID!, $officeId: ID) {
    assignAgentToOffice(agentAccountId: $agentAccountId, officeId: $officeId) {
      id
      office { id name }
    }
  }
`

/** Place an agent in an office (null unassigns). Seat limits enforced server-side. */
export async function assignAgentToOffice(
  client: GraphQLClient,
  params: { agentAccountId: string; officeId: string | null },
): Promise<void> {
  await client.request(ASSIGN_AGENT_TO_OFFICE, {
    agentAccountId: params.agentAccountId,
    officeId: params.officeId,
  })
}

const SET_AGENT_RUN_STATE = /* GraphQL */ `
  mutation GlassOfficeSetRunState($agentAccountId: ID!, $runState: AgentRunState!) {
    setAgentRunState(agentAccountId: $agentAccountId, runState: $runState) {
      id
      runState
    }
  }
`

/** RUNNING | PAUSED | STOPPED — RUNNING requires the agent to be in an office. */
export async function setAgentRunState(
  client: GraphQLClient,
  params: { agentAccountId: string; runState: 'RUNNING' | 'PAUSED' | 'STOPPED' },
): Promise<void> {
  await client.request(SET_AGENT_RUN_STATE, {
    agentAccountId: params.agentAccountId,
    runState: params.runState,
  })
}

const REQUEST_AGENT_RESTART = /* GraphQL */ `
  mutation GlassOfficeRestartAgent($agentAccountId: ID!) {
    requestAgentRestart(agentAccountId: $agentAccountId) {
      id
      runState
      restartNonce
    }
  }
`

/** Bump the restart nonce — the box kills + relaunches on its next sync. */
export async function requestAgentRestart(
  client: GraphQLClient,
  agentAccountId: string,
): Promise<void> {
  await client.request(REQUEST_AGENT_RESTART, { agentAccountId })
}

const UPDATE_AGENT_PROFILE = /* GraphQL */ `
  mutation GlassOfficeUpdateAgent($input: UpdateAgentProfileInput!) {
    updateAgentProfile(input: $input) {
      id
      jobTitle
      jobDescription
      user { id name }
    }
  }
`

/** Tweak an existing agent (org owner only): name, title, charter. */
export async function updateAgentProfile(
  client: GraphQLClient,
  params: {
    agentAccountId: string
    name?: string
    jobTitle?: string
    jobDescription?: string
  },
): Promise<void> {
  await client.request(UPDATE_AGENT_PROFILE, {
    input: {
      agentAccountId: params.agentAccountId,
      ...(params.name !== undefined ? { name: params.name } : {}),
      ...(params.jobTitle !== undefined ? { jobTitle: params.jobTitle } : {}),
      ...(params.jobDescription !== undefined
        ? { jobDescription: params.jobDescription }
        : {}),
    },
  })
}

const SET_AGENT_STATUS = /* GraphQL */ `
  mutation GlassOfficeSetAgentStatus($agentAccountId: ID!, $status: AgentStatus!) {
    setAgentStatus(agentAccountId: $agentAccountId, status: $status) {
      id
      status
    }
  }
`

/** ACTIVE / SUSPENDED / RETIRED — suspend blocks the agent's login (Auth0)
 *  before flipping local state; reactivate with ACTIVE. No hard delete exists. */
export async function setAgentStatus(
  client: GraphQLClient,
  params: { agentAccountId: string; status: 'ACTIVE' | 'SUSPENDED' | 'RETIRED' },
): Promise<void> {
  await client.request(SET_AGENT_STATUS, {
    agentAccountId: params.agentAccountId,
    status: params.status,
  })
}

/** Add a user to a pulse as STAFF. Requires the caller to be a pulse
 *  owner/admin of that room (PulseMemberPolicy::create). */
export async function joinPulse(
  client: GraphQLClient,
  params: { pulseId: string; userId: string },
): Promise<void> {
  await client.request(CREATE_PULSE_MEMBER, {
    pulseId: params.pulseId,
    input: [{ userId: params.userId, role: 'STAFF' }],
  })
}

export async function hireAgent(
  client: GraphQLClient,
  params: {
    organizationId: string
    pulseId: string
    name: string
    jobTitle?: string
    jobDescription?: string
  },
): Promise<HiredAgent> {
  const res = await client.request<{
    addAgentToOrganization?: {
      agentAccount?: {
        id: string
        userId: string
        user?: { id: string; name?: string | null } | null
      } | null
      credential?: { email: string; password: string } | null
    } | null
  }>(ADD_AGENT, {
    input: {
      organizationId: params.organizationId,
      name: params.name,
      ...(params.jobTitle ? { jobTitle: params.jobTitle } : {}),
      ...(params.jobDescription ? { jobDescription: params.jobDescription } : {}),
    },
  })
  const acct = res.addAgentToOrganization?.agentAccount
  if (!acct) throw new Error('The agent could not be created')

  // Seat them in the room. If this step fails the agent still exists (manageable
  // from the dashboard), so report the reason rather than throw.
  let joinedRoom = true
  let joinError: string | null = null
  try {
    await joinPulse(client, { pulseId: params.pulseId, userId: acct.userId })
  } catch (e) {
    joinedRoom = false
    joinError = e instanceof Error ? e.message : 'join failed'
  }

  return {
    agentAccountId: acct.id,
    userId: acct.userId,
    name: acct.user?.name ?? params.name,
    credential: res.addAgentToOrganization?.credential ?? null,
    joinedRoom,
    joinError,
  }
}
